import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import OptionRowsEditor from "../../components/admin/OptionRowsEditor";
import TableScroll from "../../components/TableScroll";
import { fetchCategoryAssessment } from "../../services/assessmentApi";
import { fetchCategories } from "../../services/deviceApi";
import {
  AGE_QUESTION_TEMPLATE,
  DEFAULT_AGE_OPTION_ROWS,
  QUESTION_GROUP_PRESETS,
  createCategoryQuestion,
  deleteQuestion,
  formatOptionImpacts,
  optionRowsFromQuestion,
  optionRowsToPayload,
  updateQuestion,
} from "../../services/questionApi";
import Pagination from "../../components/admin/Pagination";
import "../../styles/admin-common.css";

const QUESTION_TYPES = ["SINGLE_CHOICE", "MULTIPLE_CHOICE", "BOOLEAN", "TEXT"];
const PAGE_SIZE = 20;

const emptyForm = {
  question: "",
  type: "SINGLE_CHOICE",
  optionRows: DEFAULT_AGE_OPTION_ROWS.map((r) => ({ ...r })),
  priceImpact: "",
  category: "age",
  displayOrder: "",
  isRequired: true,
};

const Questions = () => {
  const [searchParams] = useSearchParams();
  const legacyDeviceId = searchParams.get("deviceId");

  const [categories, setCategories] = useState([]);
  const [productTypeId, setProductTypeId] = useState(searchParams.get("categoryId") || "");
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    fetchCategories().then((cats) => {
      setCategories(cats);
      setProductTypeId((current) => {
        if (current) return current;
        if (cats.length === 0) return current;
        const mobile = cats.find((c) => c.name?.toLowerCase() === "mobile") || cats[0];
        return String(mobile.id);
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [productTypeId]);

  const loadQuestions = async () => {
    if (!productTypeId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchCategoryAssessment(productTypeId);
      setQuestions(data.questions || []);
    } catch (err) {
      setError(err.message);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId]);

  const buildPayload = () => ({
    question: form.question,
    type: form.type,
    ...(form.type === "SINGLE_CHOICE" || form.type === "MULTIPLE_CHOICE"
      ? optionRowsToPayload(form.optionRows)
      : {}),
    priceImpact: form.priceImpact ? parseFloat(form.priceImpact) : 0,
    category: form.category || "general",
    displayOrder: form.displayOrder ? parseInt(form.displayOrder, 10) : 0,
    isRequired: form.isRequired,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      if (editingId) {
        await updateQuestion(editingId, buildPayload());
        setSuccess("Updated.");
      } else {
        await createCategoryQuestion(productTypeId, buildPayload());
        setSuccess("Added to product type catalog.");
      }
      setForm(emptyForm);
      setEditingId(null);
      await loadQuestions();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setForm({
      question: q.question,
      type: q.type,
      optionRows: optionRowsFromQuestion(q),
      priceImpact: String(q.priceImpact ?? ""),
      category: q.category || "general",
      displayOrder: q.displayOrder != null ? String(q.displayOrder) : "",
      isRequired: q.isRequired !== false,
    });
  };

  const applyAgeTemplate = () => {
    setForm((prev) => ({
      ...prev,
      question: AGE_QUESTION_TEMPLATE.question,
      type: AGE_QUESTION_TEMPLATE.type,
      category: "age",
      optionRows: DEFAULT_AGE_OPTION_ROWS.map((r) => ({ ...r })),
      priceImpact: "0",
    }));
  };

  const productTypeName = categories.find((c) => String(c.id) === String(productTypeId))?.name || "—";
  const needsOptions = form.type === "SINGLE_CHOICE" || form.type === "MULTIPLE_CHOICE";
  const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
  const pageQuestions = questions.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  if (legacyDeviceId) {
    return <Navigate to={`/models/${legacyDeviceId}/questions`} replace />;
  }

  return (
    <div className="admin-page">
      <h2>Central question catalog</h2>
      <p className="subtitle">
        Define questions once per product type. All models inherit these automatically.{" "}
        Per-model impact changes are on the <Link to="/models">Models</Link> page → Questions.
      </p>

      {error && <p className="error-msg">{error}</p>}
      {success && <p className="success-msg">{success}</p>}

      <div className="admin-filters">
        <label className="field-label">
          Product type
          <select value={productTypeId} onChange={(e) => { setProductTypeId(e.target.value); setEditingId(null); }}>
            <option value="">Select type</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </div>

      {productTypeId && (
        <>
          <div className="admin-card">
            <h3>{editingId ? "Edit question" : `Add question — ${productTypeName}`}</h3>
            <form className="admin-form admin-form-grid" onSubmit={handleSubmit}>
              <label className="field-label span-2">
                Question
                <textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={2} required />
              </label>
              <label className="field-label">
                Type
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {QUESTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="field-label">
                Group
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {QUESTION_GROUP_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              {needsOptions && (
                <div className="span-2">
                  <p className="field-label" style={{ marginBottom: 8 }}>Answer options</p>
                  <OptionRowsEditor
                    rows={form.optionRows}
                    onChange={(optionRows) => setForm({ ...form, optionRows })}
                  />
                </div>
              )}
              <label className="field-label">
                Default impact %
                <input type="number" value={form.priceImpact} onChange={(e) => setForm({ ...form, priceImpact: e.target.value })} />
              </label>
              <div className="form-actions span-2">
                <button type="submit" className="admin-btn">{editingId ? "Update" : "Save"}</button>
                {!editingId && (
                  <>
                    <button type="button" className="admin-btn secondary" onClick={applyAgeTemplate}>
                      Fill age template
                    </button>
                    <button type="button" className="admin-btn secondary" onClick={async () => {
                      await createCategoryQuestion(productTypeId, AGE_QUESTION_TEMPLATE);
                      setSuccess("Age question added.");
                      loadQuestions();
                    }}>+ Save age template</button>
                  </>
                )}
                {editingId && (
                  <button type="button" className="admin-btn secondary" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancel</button>
                )}
              </div>
            </form>
          </div>

          {loading ? <div className="admin-loading">Loading</div> : (
            <>
              {questions.length > 0 && (
                <p className="subtitle">{questions.length} question{questions.length === 1 ? "" : "s"}</p>
              )}
              <TableScroll>
                <table className="admin-table">
                  <thead>
                    <tr><th>#</th><th>Group</th><th>Question</th><th>Type</th><th>Options</th><th>Impacts</th><th></th></tr>
                  </thead>
                  <tbody>
                    {pageQuestions.length === 0 && (
                      <tr><td colSpan={7} className="empty-msg">No questions for this product type</td></tr>
                    )}
                    {pageQuestions.map((q) => (
                      <tr key={q.id}>
                        <td>{q.displayOrder}</td>
                        <td><code>{q.category}</code></td>
                        <td>{q.question}</td>
                        <td>{q.type}</td>
                        <td><small>{(q.options || []).map((o) => q.optionLabels?.[o] || o).join(" · ")}</small></td>
                        <td><small>{formatOptionImpacts(q.optionImpacts)}</small></td>
                        <td className="actions-cell">
                          <button type="button" className="admin-btn secondary" onClick={() => startEdit(q)}>Edit</button>
                          <button type="button" className="admin-btn danger" onClick={async () => {
                            if (window.confirm("Deactivate this question for the whole product type?")) {
                              await deleteQuestion(q.id);
                              loadQuestions();
                            }
                          }}>Deactivate</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              <Pagination page={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Questions;
